import React, { useMemo, useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Preview as PreviewIcon,
  Download as DownloadIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RestartAlt as ResetZoomIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  Autorenew as ReloadIcon
} from '@mui/icons-material';
import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist/build/pdf';
import { downloadFile } from '../../utils/fileUtils';

/**
 * Simple file size formatter
 */
const formatFileSize = (bytes) => {
  if (!bytes) return '';
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${Math.round(bytes / Math.pow(1024, i) * 100) / 100} ${sizes[i]}`;
};

/**
 * Converts ArrayBuffer to string with error handling
 */
const arrayBufferToString = (arrayBuffer) => {
  if (!arrayBuffer || !(arrayBuffer instanceof ArrayBuffer)) return null;
  try {
    // Use TextDecoder for proper UTF-8 handling
    const decoder = new TextDecoder('utf-8');
    return decoder.decode(arrayBuffer);
  } catch (error) {
    console.warn('Invalid ArrayBuffer content:', error);
    return null;
  }
};

/**
 * Helper function to create Blob URL from ArrayBuffer
 * Note: Caller is responsible for cleanup using URL.revokeObjectURL
 * @param {ArrayBuffer} arrayBuffer - The file content
 * @param {string} mimeType - The MIME type
 * @returns {string|null} The Blob URL or null
 */
const createBlobUrl = (arrayBuffer, mimeType) => {
  if (!arrayBuffer || !(arrayBuffer instanceof ArrayBuffer)) return null;
  try {
    const blob = new Blob([arrayBuffer], { type: mimeType });
    return URL.createObjectURL(blob);
  } catch (error) {
    console.warn('Failed to create Blob URL:', error);
    return null;
  }
};

/**
 * Hook for managing a single Blob URL with automatic cleanup
 * @param {ArrayBuffer} arrayBuffer - The file content
 * @param {string} mimeType - The MIME type
 * @returns {string|null} The Blob URL or null
 */
const useBlobUrl = (arrayBuffer, mimeType) => {
  const blobUrl = useMemo(() => createBlobUrl(arrayBuffer, mimeType), [arrayBuffer, mimeType]);

  // Cleanup on unmount or when URL changes
  useEffect(() => {
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl]);

  return blobUrl;
};

/**
 * Individual renderer components that can use hooks properly
 */
const ImageRenderer = ({ file, content }) => {
  const blobUrl = useBlobUrl(content, file.mimeType);
  
  if (!blobUrl || (content instanceof ArrayBuffer && content.byteLength === 0)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Typography color="text.secondary">
          {content instanceof ArrayBuffer && content.byteLength === 0 
            ? 'Image file is empty' 
            : 'Unable to display image'
          }
        </Typography>
      </Box>
    );
  }
  
  return (
    <Box sx={{ position: 'relative', height: '100%' }}>
      <Box sx={{ 
        textAlign: 'center', 
        p: 2,
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <img 
          src={blobUrl}
          alt={file.name || file.originalName} 
          style={{ 
            maxWidth: '100%', 
            maxHeight: '100%', 
            objectFit: 'contain',
            pointerEvents: 'none',
          }} 
        />
      </Box>
      <Box
        onPointerDown={(e) => e.stopPropagation()}
        onPointerUp={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, pointerEvents: 'auto' }}
      >
        <DownloadButton file={file} content={content} />
      </Box>
    </Box>
  );
};

const pdfWorkerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url);

const normalisePdfData = (value) => {
  if (!value) {
    return null;
  }
  if (value instanceof Uint8Array) {
    return new Uint8Array(value); // clone to avoid transferring original buffer
  }
  if (value instanceof ArrayBuffer) {
    return new Uint8Array(value.slice(0));
  }
  if (ArrayBuffer.isView(value)) {
    const { buffer, byteOffset, byteLength } = value;
    return new Uint8Array(buffer.slice(byteOffset, byteOffset + byteLength));
  }
  return null;
};

const ensurePdfWorkerConfigured = (() => {
  let configured = false;
  return () => {
    if (configured) return;
    if (typeof window !== 'undefined') {
      GlobalWorkerOptions.workerSrc = pdfWorkerSrc.toString();
      configured = true;
    }
  };
})();

const PdfRenderer = ({ file, content, interactivePreview, onReloadContent }) => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const controlsRef = useRef(null);
  const [isRendering, setIsRendering] = useState(true);
  const [renderError, setRenderError] = useState(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const pdfDocumentRef = useRef(null);
  const [numPages, setNumPages] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [isPanning, setIsPanning] = useState(false);
  const panStateRef = useRef({ active: false, pointerId: null, startX: 0, startY: 0, scrollLeft: 0, scrollTop: 0 });
  const [documentVersion, setDocumentVersion] = useState(0);

  useEffect(() => () => {
    if (pdfDocumentRef.current && typeof pdfDocumentRef.current.destroy === 'function') {
      pdfDocumentRef.current.destroy();
      pdfDocumentRef.current = null;
    }
  }, []);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) {
      return undefined;
    }

    const updateSize = () => {
      const nextWidth = element.clientWidth || 0;
      const nextHeight = element.clientHeight || 0;
      setContainerSize((prev) => {
        if (prev.width === nextWidth && prev.height === nextHeight) {
          return prev;
        }
        return { width: nextWidth, height: nextHeight };
      });
    };

    updateSize();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(updateSize);
      observer.observe(element);
      return () => observer.disconnect();
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', updateSize);
      return () => window.removeEventListener('resize', updateSize);
    }

    return undefined;
  }, []);

  useEffect(() => {
    setPageNumber(1);
    setZoom(1);
  }, [content]);

  useEffect(() => {
    let cancelled = false;
    let loadedDocument;

    const loadDocument = async () => {
      if (!content) {
        setRenderError("Anteprima PDF non disponibile");
        setIsRendering(false);
        return;
      }

      try {
        setIsRendering(true);
        setRenderError(null);
        ensurePdfWorkerConfigured();

        const data = normalisePdfData(content);
        if (!data || data.byteLength === 0) {
          setRenderError('Nessun contenuto PDF disponibile');
          setIsRendering(false);
          return;
        }

        if (pdfDocumentRef.current) {
          try {
            pdfDocumentRef.current.destroy();
          } catch (destroyError) {
            console.debug('Errore durante il destroy del PDF esistente', destroyError);
          }
          pdfDocumentRef.current = null;
        }

        loadedDocument = await getDocument({ data }).promise;
        if (cancelled) {
          loadedDocument?.destroy();
          return;
        }

        pdfDocumentRef.current = loadedDocument;
        const total = loadedDocument.numPages || 1;
        setNumPages(total);
        setPageNumber((prev) => (prev > total ? total : prev));
        setDocumentVersion((prev) => prev + 1);
      } catch (error) {
        if (!cancelled) {
          console.error('Errore nel caricamento del PDF', error);
          setRenderError("Impossibile caricare il PDF");
          setIsRendering(false);
        pdfDocumentRef.current = null;
        }
      }
    };

    loadDocument();

    return () => {
      cancelled = true;
      if (loadedDocument && typeof loadedDocument.destroy === 'function') {
        loadedDocument.destroy();
      }
    };
  }, [content]);

  useEffect(() => {
    const pdfDocument = pdfDocumentRef.current;
    if (!pdfDocument) {
      return undefined;
    }
    if (!canvasRef.current) {
      return undefined;
    }
    if (!containerSize.width || !containerSize.height) {
      return undefined;
    }

    let cancelled = false;
    let renderTask;
    let page;

    const renderPage = async () => {
      setIsRendering(true);

      try {
        const targetPage = interactivePreview ? pageNumber : 1;
        page = await pdfDocument.getPage(targetPage);
        if (!page || cancelled) {
          return;
        }

        const viewport = page.getViewport({ scale: 1 });
        const containerWidth = containerSize.width;
        const containerHeight = containerSize.height;
        const widthScale = containerWidth / viewport.width;
        const heightScale = containerHeight / viewport.height;
        const baseScale = Number.isFinite(widthScale) && Number.isFinite(heightScale)
          ? Math.max(Math.min(widthScale, heightScale), 0.1)
          : 1;
        const effectiveScale = interactivePreview ? baseScale * zoom : baseScale;
        const scaledViewport = page.getViewport({ scale: effectiveScale });

        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');
        const outputScale = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;

        canvas.width = Math.ceil(scaledViewport.width * outputScale);
        canvas.height = Math.ceil(scaledViewport.height * outputScale);
        context.setTransform(outputScale, 0, 0, outputScale, 0, 0);
        context.clearRect(0, 0, canvas.width, canvas.height);

        renderTask = page.render({ canvasContext: context, viewport: scaledViewport });
        await renderTask.promise;

        if (!cancelled) {
          setIsRendering(false);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Errore nel rendering del PDF', error);
          setRenderError("Impossibile visualizzare l'anteprima PDF");
          setIsRendering(false);
        }
      } finally {
        if (page && typeof page.cleanup === 'function') {
          page.cleanup();
        }
      }
    };

    renderPage();

    return () => {
      cancelled = true;
      if (renderTask && typeof renderTask.cancel === 'function') {
        renderTask.cancel();
      }
      if (page && typeof page.cleanup === 'function') {
        page.cleanup();
      }
    };
  }, [containerSize, pageNumber, zoom, interactivePreview, documentVersion]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !interactivePreview) {
      setIsPanning(false);
      panStateRef.current = { active: false, pointerId: null, startX: 0, startY: 0, scrollLeft: 0, scrollTop: 0 };
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (event.button !== 0) return;
      // Ignore pointer down if it originated inside any control overlay
      const controlsEl = controlsRef.current;
      const isInControls = (controlsEl && controlsEl.contains(event.target)) ||
        (typeof event.target?.closest === 'function' && event.target.closest('[data-fileviewer-control="true"]'));
      if (isInControls) {
        return;
      }
      panStateRef.current = {
        active: true,
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        scrollLeft: container.scrollLeft,
        scrollTop: container.scrollTop,
      };
      try {
        container.setPointerCapture(event.pointerId);
      } catch (captureError) {
        console.debug('Pointer capture error', captureError);
      }
      setIsPanning(true);
    };

    const handlePointerMove = (event) => {
      const state = panStateRef.current;
      if (!state.active || event.pointerId !== state.pointerId) return;
      const dx = event.clientX - state.startX;
      const dy = event.clientY - state.startY;
      container.scrollLeft = state.scrollLeft - dx;
      container.scrollTop = state.scrollTop - dy;
    };

    const endPan = (event) => {
      const state = panStateRef.current;
      if (!state.active) return;
      if (event.type !== 'mouseleave' && event.pointerId !== state.pointerId) {
        return;
      }
      if (state.pointerId !== null) {
        try {
          container.releasePointerCapture(state.pointerId);
        } catch (releaseError) {
          console.debug('Pointer release error', releaseError);
        }
      }
      panStateRef.current = { active: false, pointerId: null, startX: 0, startY: 0, scrollLeft: 0, scrollTop: 0 };
      setIsPanning(false);
    };

    container.addEventListener('pointerdown', handlePointerDown);
    container.addEventListener('pointermove', handlePointerMove);
    container.addEventListener('pointerup', endPan);
    container.addEventListener('pointercancel', endPan);
    container.addEventListener('mouseleave', endPan);

    return () => {
      container.removeEventListener('pointerdown', handlePointerDown);
      container.removeEventListener('pointermove', handlePointerMove);
      container.removeEventListener('pointerup', endPan);
      container.removeEventListener('pointercancel', endPan);
      container.removeEventListener('mouseleave', endPan);
      panStateRef.current = { active: false, pointerId: null, startX: 0, startY: 0, scrollLeft: 0, scrollTop: 0 };
      setIsPanning(false);
    };
  }, [interactivePreview]);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        height: '100%',
        width: '100%',
        display: 'flex',
        alignItems: interactivePreview ? 'flex-start' : 'center',
        justifyContent: interactivePreview ? 'flex-start' : 'center',
        backgroundColor: 'grey.100',
        overflow: interactivePreview ? 'auto' : 'hidden',
        cursor: interactivePreview ? (isPanning ? 'grabbing' : 'grab') : 'default',
      }}
      onClickCapture={(e) => { console.log('[FileRenderer][PDF] container onClickCapture', { target: e.target?.tagName, fileId: file?.id }); }}
    >
      {renderError ? (
        <Typography color="text.secondary">{renderError}</Typography>
      ) : (
        <canvas
          ref={canvasRef}
          style={{
            opacity: isRendering ? 0 : 1,
            transition: 'opacity 0.2s ease',
            display: 'block',
            pointerEvents: 'none',
          }}
        />
      )}

      {interactivePreview && !renderError && (
        <Box
          ref={controlsRef}
          data-fileviewer-control="true"
          onPointerDown={(e) => { e.stopPropagation(); }}
          onPointerUp={(e) => { e.stopPropagation(); }}
          onMouseDown={(e) => { e.stopPropagation(); }}
          onMouseUp={(e) => { e.stopPropagation(); }}
          onClick={(e) => { console.log('[FileRenderer][PDF] Control panel click container', { fileId: file?.id }); e.stopPropagation(); }}
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            backgroundColor: 'rgba(255,255,255,0.9)',
            borderRadius: 1,
            p: 1,
            boxShadow: 1,
            zIndex: 2000,
            pointerEvents: 'auto',
          }}
        >
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Zoom out">
            <IconButton
              size="small"
              onClickCapture={() => { console.log('[FileRenderer][PDF] Zoom out click capture', { fileId: file?.id }); }}
              onClick={() => {
                console.log('[FileRenderer][PDF] Zoom out clicked', { fileId: file?.id });
                setZoom((prev) => Math.max(prev - 0.25, 0.5));
              }}
              sx={{ position: 'relative', zIndex: 3001, pointerEvents: 'auto' }}
            >
              <ZoomOutIcon fontSize="small" />
            </IconButton>
            </Tooltip>
            <Tooltip title="Reset zoom">
            <IconButton
              size="small"
              onClickCapture={() => { console.log('[FileRenderer][PDF] Reset zoom click capture', { fileId: file?.id }); }}
              onClick={() => {
                console.log('[FileRenderer][PDF] Reset zoom clicked', { fileId: file?.id });
                setZoom(1);
              }}
              sx={{ position: 'relative', zIndex: 3001, pointerEvents: 'auto' }}
            >
              <ResetZoomIcon fontSize="small" />
            </IconButton>
            </Tooltip>
            <Tooltip title="Zoom in">
            <IconButton
              size="small"
              onClickCapture={() => { console.log('[FileRenderer][PDF] Zoom in click capture', { fileId: file?.id }); }}
              onClick={() => {
                console.log('[FileRenderer][PDF] Zoom in clicked', { fileId: file?.id });
                setZoom((prev) => Math.min(prev + 0.25, 3));
              }}
              sx={{ position: 'relative', zIndex: 3001, pointerEvents: 'auto' }}
            >
              <ZoomInIcon fontSize="small" />
            </IconButton>
            </Tooltip>
            {/* Reload removed per product decision */}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title="Previous page">
            <IconButton
              size="small"
              onClickCapture={() => { console.log('[FileRenderer][PDF] Prev page click capture', { fileId: file?.id }); }}
              onClick={() => {
                console.log('[FileRenderer][PDF] Prev page clicked', { fileId: file?.id });
                setPageNumber((prev) => Math.max(prev - 1, 1));
              }}
              sx={{ position: 'relative', zIndex: 3001, pointerEvents: 'auto' }}
              disabled={pageNumber <= 1}
            >
              <NavigateBeforeIcon fontSize="small" />
            </IconButton>
            </Tooltip>
            <Typography variant="caption" sx={{ minWidth: 70, textAlign: 'center' }}>
              {pageNumber} / {numPages}
            </Typography>
            <Tooltip title="Next page">
            <IconButton
              size="small"
              onClickCapture={() => { console.log('[FileRenderer][PDF] Next page click capture', { fileId: file?.id }); }}
              onClick={() => {
                console.log('[FileRenderer][PDF] Next page clicked', { fileId: file?.id });
                setPageNumber((prev) => Math.min(prev + 1, numPages));
              }}
              sx={{ position: 'relative', zIndex: 3001, pointerEvents: 'auto' }}
              disabled={pageNumber >= numPages}
            >
              <NavigateNextIcon fontSize="small" />
            </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}

      {isRendering && !renderError && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <CircularProgress size={32} />
        </Box>
      )}

      <Box
        onPointerDown={(e) => e.stopPropagation()}
        onPointerUp={(e) => e.stopPropagation()}
        onClickCapture={(e) => { console.log('[FileRenderer] Right overlay click capture', { target: e.target?.tagName, fileId: file?.id }); }}
        onClick={(e) => e.stopPropagation()}
        data-fileviewer-control="true"
        sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2000, pointerEvents: 'auto' }}
      >
        <DownloadButton file={file} content={content} />
      </Box>
    </Box>
  );
};

const TextRenderer = ({ file, content }) => {
  const textContent = arrayBufferToString(content);
  const isEmpty = content instanceof ArrayBuffer && content.byteLength === 0;
  
  return (
    <Box sx={{ position: 'relative', height: '100%' }}>
      <Box sx={{ 
        p: 2, 
        height: '100%', 
        overflow: 'auto',
        fontFamily: 'monospace',
        fontSize: '0.875rem',
        lineHeight: 1.5,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word'
      }}>
        {isEmpty ? 'File is empty (0 bytes)' : (textContent || 'No content available')}
      </Box>
      <Box
        onPointerDown={(e) => e.stopPropagation()}
        onPointerUp={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, pointerEvents: 'auto' }}
      >
        <DownloadButton file={file} content={content} />
      </Box>
    </Box>
  );
};

const JsonRenderer = ({ file, content }) => {
  const textContent = arrayBufferToString(content);
  const isEmpty = content instanceof ArrayBuffer && content.byteLength === 0;
  let parsedContent;
  
  if (!isEmpty) {
    try {
      parsedContent = textContent ? JSON.parse(textContent) : null;
    } catch (error) {
      console.warn('Invalid JSON content:', error);
      parsedContent = null;
    }
  }

  return (
    <Box sx={{ position: 'relative', height: '100%' }}>
      <Box sx={{ 
        p: 2, 
        height: '100%', 
        overflow: 'auto',
        fontFamily: 'monospace',
        fontSize: '0.875rem',
        lineHeight: 1.5,
      }}>
        <pre style={{ margin: 0 }}>
          {isEmpty 
            ? 'JSON file is empty (0 bytes)'
            : (parsedContent 
                ? JSON.stringify(parsedContent, null, 2)
                : 'Invalid JSON content'
              )
          }
        </pre>
      </Box>
      <Box
        onPointerDown={(e) => e.stopPropagation()}
        onPointerUp={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, pointerEvents: 'auto' }}
      >
        <DownloadButton file={file} content={content} />
      </Box>
    </Box>
  );
};

const XmlRenderer = ({ file, content }) => {
  const textContent = arrayBufferToString(content);
  const isEmpty = content instanceof ArrayBuffer && content.byteLength === 0;
  
  // Basic XML formatting function
  const formatXml = (xml) => {
    if (!xml) return xml;
    
    try {
      const formatted = xml
        .replace(/(>)(<)(\/*)/g, '$1\n$2$3')
        .replace(/(\w+)="([^"]*)"/g, '$1="$2"')
        .replace(/>\s*</g, '>\n<');
      
      // Add indentation
      const lines = formatted.split('\n');
      let indent = 0;
      const indentedLines = lines.map(line => {
        const trimmedLine = line.trim();
        if (trimmedLine.length === 0) return '';
        
        if (trimmedLine.startsWith('</')) {
          indent = Math.max(0, indent - 1);
        }
        
        const indentedLine = '  '.repeat(indent) + trimmedLine;
        
        if (trimmedLine.startsWith('<') && !trimmedLine.startsWith('</') && !trimmedLine.endsWith('/>')) {
          indent++;
        }
        
        return indentedLine;
      });
      
      return indentedLines.join('\n');
    } catch (error) {
      console.warn('XML formatting failed:', error);
      return xml;
    }
  };

  return (
    <Box sx={{ position: 'relative', height: '100%' }}>
      <Box sx={{ 
        p: 2, 
        height: '100%', 
        overflow: 'auto',
        fontFamily: 'monospace',
        fontSize: '0.875rem',
        lineHeight: 1.5,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        backgroundColor: '#f8f9fa'
      }}>
        {isEmpty ? 'XML file is empty (0 bytes)' : formatXml(textContent) || 'No content available'}
      </Box>
      <Box
        onPointerDown={(e) => e.stopPropagation()}
        onPointerUp={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, pointerEvents: 'auto' }}
      >
        <DownloadButton file={file} content={content} />
      </Box>
    </Box>
  );
};

const DownloadButton = ({ file, content }) => {
  const handleDownload = (event) => {
    // Allow the click to proceed but stop it from triggering container panning
    event.stopPropagation();
    
    if (!content || !file) return;
    
    try {
      console.log('[FileRenderer] Download clicked', { fileId: file?.id, name: file?.name || file?.originalName });
      const filename = file.name || file.originalName || 'download';
      downloadFile(content, filename, file.mimeType || 'application/octet-stream');
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  if (!content || (content instanceof ArrayBuffer && content.byteLength === 0)) {
    return null;
  }

  return (
    <Tooltip title="Download file">
      <IconButton
        onClickCapture={() => { console.log('[FileRenderer] Download click capture', { fileId: file?.id }); }}
        onClick={handleDownload}
        color="primary"
        size="small"
        data-fileviewer-control="true"
        sx={{ 
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
          zIndex: 3001,
          pointerEvents: 'auto'
        }}
      >
        <DownloadIcon />
      </IconButton>
    </Tooltip>
  );
};

const DefaultRenderer = ({ file, content, CustomActions }) => {
  const isEmptyFile = file.size === 0;
  
  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center',
      p: 5,
      height: '100%',
    }}>
      <PreviewIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
      <Typography variant="h6" gutterBottom>
        {file.name || file.originalName}
      </Typography>
      {file.size !== undefined && (
        <Typography color="text.secondary" gutterBottom>
          {formatFileSize(file.size)}
        </Typography>
      )}
      <Typography color="text.secondary">
        {isEmptyFile 
          ? 'This file is empty (0 bytes)'
          : 'Preview not available for this file type'
        }
      </Typography>
      {CustomActions && (
        <Box sx={{ position: 'absolute', bottom: 16, right: 16 }}>
          <CustomActions file={file} />
        </Box>
      )}
      {!isEmptyFile && (
        <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
          <DownloadButton file={file} content={content} />
        </Box>
      )}
    </Box>
  );
};

/**
 * Strategy pattern for file rendering using React components
 */
const FileRenderers = {
  image: ImageRenderer,
  pdf: PdfRenderer,
  text: TextRenderer,
  json: JsonRenderer,
  xml: XmlRenderer,
  default: DefaultRenderer
};

/**
 * File type classifier - can be customized via props
 */
const classifyFileType = (mimeType, customPatterns = {}, file = null) => {
  const normalisedMime = typeof mimeType === 'string'
    ? mimeType.split(';')[0].trim().toLowerCase()
    : '';

  // Default patterns with broader matches
  const defaultPatterns = {
    image: /^image\//,
    pdf: /^application\/(pdf|x-pdf)$/,
    json: /^application\/json$/,
    xml: /^(application|text)\/(xml|.*\+xml)$/
  };

  const patterns = { ...defaultPatterns, ...customPatterns };

  if (normalisedMime) {
    if (patterns.image && patterns.image.test(normalisedMime)) return 'image';
    if (patterns.pdf && patterns.pdf.test(normalisedMime)) return 'pdf';
    if (patterns.json && patterns.json.test(normalisedMime)) return 'json';
    if (patterns.xml && patterns.xml.test(normalisedMime)) return 'xml';
    if (normalisedMime.startsWith('text/')) return 'text';
  }

  const fileName = file?.name || file?.originalName;
  if (fileName) {
    const lowerName = fileName.toLowerCase();
    if (lowerName.endsWith('.pdf')) return 'pdf';
    if (/\.(png|jpe?g|gif|bmp|svg)$/.test(lowerName)) return 'image';
    if (lowerName.endsWith('.json')) return 'json';
    if (lowerName.endsWith('.xml')) return 'xml';
    if (/\.(txt|md|csv|log)$/i.test(lowerName)) return 'text';
  }

  return 'default';
};

/**
 * FileRenderer - Generic file content renderer using strategy pattern
 * 
 * @param {object} props
 * @param {object} props.file - File object with metadata (must have mimeType and name/originalName)
 * @param {ArrayBuffer} props.content - File content as ArrayBuffer
 * @param {boolean} props.loading - Whether content is loading
 * @param {string} props.error - Error message if loading failed
 * @param {object} props.customRenderers - Additional renderers to extend default ones
 * @param {object} props.customPatterns - Additional MIME type patterns
 * @param {function} props.onError - Callback for render errors
 */
const FileRenderer = ({ 
  file, 
  content, 
  loading, 
  error, 
  customRenderers = {},
  customActions = {},
  customPatterns = {},
  onError,
  interactivePreview = false,
  onReloadContent,
}) => {
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        height: '100%'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        p: 3,
        height: '100%'
      }}>
        <Typography color="error" gutterBottom>
          Error loading file
        </Typography>
        <Typography color="text.secondary" variant="body2">
          {error}
        </Typography>
      </Box>
    );
  }

  if (!file) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        height: '100%'
      }}>
        <Typography color="text.secondary">No file selected</Typography>
      </Box>
    );
  }

  if (!content || (content instanceof ArrayBuffer && content.byteLength === 0)) {
    return customActions ? FileRenderers.default({ file, content, CustomActions: customActions }) : FileRenderers.default({ file, content });
  }

  try {
    const fileType = classifyFileType(file.mimeType, customPatterns, file);
    const allRenderers = { ...FileRenderers, ...customRenderers };
    const Renderer = allRenderers[fileType] || FileRenderers.default;

    if(customActions) {
      const CustomActions = customActions;
      return (
        <Box sx={{ position: 'relative', height: '100%' }}>
          <Renderer file={file} content={content} interactivePreview={interactivePreview} onReloadContent={onReloadContent} />
          <Box sx={{ position: 'absolute', bottom: 16, right: 16 }}>
            <CustomActions file={file} />
          </Box>
        </Box>
      );
    } else {
      return <Renderer file={file} content={content} interactivePreview={interactivePreview} onReloadContent={onReloadContent} />;
    }
  } catch (renderError) {
    if (onError) {
      onError(renderError, file);
    }
    return FileRenderers.default({ file, content });
  }
};

export default FileRenderer; 
